import { useEffect } from "react";

import { useSession } from "@/auth/session/SessionProvider";
import { setCurrentToken } from "./tokenStore";

/**
 * Pushes the current API token from the session into the module-scoped
 * {@link setCurrentToken} store whenever it changes.
 *
 * <p>Renders nothing. Mounted once near the root, inside the SessionProvider.</p>
 *
 * <p>Also listens for the global {@code "auth-expired"} event that the API
 * client dispatches on a 401. Recovery depends on which kind of session we
 * hold, which is exactly what {@code session.refresh()} encapsulates: a native
 * session does the refresh-token exchange through the Cognito SDK, a
 * redirect-flow session goes through silent renewal. If neither recovers (the
 * refresh token is gone or revoked) we send the user to sign in again.</p>
 */
export function TokenSync() {
  const session = useSession();
  const token = session.user?.apiToken ?? null;

  useEffect(() => {
    setCurrentToken(token);
  }, [token]);

  useEffect(() => {
    const onExpired = async () => {
      try {
        if (await session.refresh()) return;
      } catch {
        // fall through — treat a failed refresh as "no session left"
      }
      session.signIn();
    };
    window.addEventListener("auth-expired", onExpired);
    return () => window.removeEventListener("auth-expired", onExpired);
  }, [session]);

  return null;
}
